import React, { useState, useEffect } from 'react';
import { Card, Heading, Text, Flex, Avatar, Separator, Box, Dialog, Button } from '@radix-ui/themes';
import { StarIcon, TargetIcon, CheckIcon, CalendarIcon, ClockIcon } from '@radix-ui/react-icons';
import { Member, FILE_SERVER_URL, apiService, RankRecord, RankResponse, ApplyExamResponse, MemberProfile } from './services/api';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';

interface RankDetailsProps {
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

const RankDetails: React.FC<RankDetailsProps> = ({ students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [rankData, setRankData] = useState<RankResponse | null>(null);
    const [profile, setProfile] = useState<MemberProfile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [applyingForExam, setApplyingForExam] = useState(false);
    const [applyMessage, setApplyMessage] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Fetch rank data and profile for the selected student
    const fetchRankData = async (student: Member | null, preserveApplyMessage: boolean = false) => {
        if (!student) {
            debugLog('❌ No student selected, skipping fetch');
            return;
        }

        debugLog('📡 Fetching rank data for student:', student.memberId);
        setLoading(true);
        setError(null);

        // Only clear apply message if not preserving it
        if (!preserveApplyMessage) {
            setApplyMessage(null); // Clear any previous apply messages
            debugLog('🗑️ Cleared apply message during fetch');
        } else {
            debugLog('🔒 Preserving apply message during fetch');
        }

        setShowConfirmDialog(false); // Close dialog if open

        try {
            const [rankResponse, profileResponse] = await Promise.all([
                apiService.getRankDetails(student.memberId),
                apiService.getMemberProfile(student.memberId)
            ]);
            debugLog('📥 Rank data response:', rankResponse);
            debugLog('📥 Profile data response:', profileResponse);

            if (rankResponse.success) {
                debugLog('✅ Rank data fetched successfully');
                setRankData(rankResponse);
            } else {
                debugLog('❌ Failed to fetch rank details');
                setError('Failed to fetch rank details');
            }
            if (profileResponse.success) {
                setProfile(profileResponse.member);
            } else {
                setProfile(null);
            }
        } catch (err) {
            debugError('💥 Error fetching rank/profile data:', err);
            setError('Failed to fetch rank details');
        } finally {
            setLoading(false);
            debugLog('🏁 Rank data fetch completed');
        }
    };

    // Debug function to check current state
    const debugRankDetails = () => {
        debugLog('🔍 RankDetails Debug Info:');
        debugLog('Selected Student:', selectedStudent);
        debugLog('Rank Data:', rankData);
        debugLog('Loading:', loading);
        debugLog('Error:', error);
        debugLog('Applying for Exam:', applyingForExam);
        debugLog('Apply Message:', applyMessage);
        debugLog('Show Confirm Dialog:', showConfirmDialog);
        debugLog('Current Rank:', currentRank);
        debugLog('Total Ranks:', totalRanks);
        debugLog('Pending Count:', pendingCount);

        // Check cache status
        const cacheStatus = apiService.getCacheStatus();
        debugLog('Cache Status:', cacheStatus);
    };

    // Debug applyMessage changes
    React.useEffect(() => {
        debugLog('🔄 applyMessage changed:', applyMessage);
        if (applyMessage) {
            debugLog('🎯 Toast should be visible with message:', applyMessage);
            debugLog('🎯 Toast background color:', applyMessage.includes('Successfully') ? 'green' : 'red');
        }
    }, [applyMessage]);

    // Debug toast rendering
    React.useEffect(() => {
        if (applyMessage) {
            debugLog('🎯 Toast is being rendered with message:', applyMessage);
            debugLog('🎯 Toast position: fixed, top: 100px, right: 20px');
            debugLog('🎯 Toast z-index: 1001');
        }
    }, [applyMessage]);

    // Add debug function to window for easy access
    React.useEffect(() => {
        (window as any).debugRankDetails = debugRankDetails;
        (window as any).testToast = () => {
            debugLog('🧪 Testing toast notification...');
            setApplyMessage('Test success message!');
            setTimeout(() => setApplyMessage(null), 3000);
        };
        debugLog('🔧 Debug functions added to window');
    }, []);

    useEffect(() => {
        fetchRankData(selectedStudent);
    }, [selectedStudent]);

    // Calculate current rank (most recent with valid certificate)
    const currentRank = rankData?.ranks?.find(
        (rank) => rank.certificateNumber && rank.certificateNumber !== 'Pending'
    ) || null;
    const totalRanks = rankData?.ranks?.length || 0;
    const progressPercentage = totalRanks > 0 ? Math.round((totalRanks / 13) * 100) : 0; // Assuming 13 total possible ranks

    // Count pending applications
    const pendingApplications = rankData?.ranks?.filter(
        (rank) => !rank.certificateNumber || rank.certificateNumber === 'Pending'
    ) || [];
    const pendingCount = pendingApplications.length;

    // Function to get rank color based on rank name
    const getRankColor = (rankName: string): string => {
        const rank = rankName.toLowerCase();
        if (rank.includes('black')) return 'var(--gray-12)'; // Black ranks
        if (rank.includes('brown')) return 'var(--brown-9)'; // Brown ranks
        if (rank.includes('purple')) return 'var(--purple-9)'; // Purple rank
        if (rank.includes('blue')) return 'var(--blue-9)'; // Blue rank
        if (rank.includes('green')) return 'var(--green-9)'; // Green rank
        if (rank.includes('orange')) return 'var(--orange-9)'; // Orange rank
        if (rank.includes('yellow')) return 'var(--yellow-9)'; // Yellow rank
        if (rank.includes('white')) return 'var(--gray-6)'; // White ranks
        return 'var(--gray-9)'; // Default color
    };

    // Function to show confirmation dialog
    const handleApplyClick = () => {
        if (!selectedStudent || !rankData?.nextRankId) {
            setApplyMessage('No exam available to apply for');
            return;
        }
        if (profile?.membershipStatus === 'EXPIRED') {
            setApplyMessage('You must have an active membership to apply for the next rank.');
            return;
        }
        setShowConfirmDialog(true);
    };

    // Function to handle applying for exam
    const handleApplyForExam = async () => {
        debugLog('🚀 Starting exam application process...');
        setShowConfirmDialog(false);
        setApplyingForExam(true);
        setApplyMessage(null);

        try {
            debugLog('📡 Making API call to apply for exam...');
            debugLog('Student ID:', selectedStudent!.memberId);
            debugLog('Next Rank ID:', rankData!.nextRankId);

            const response: ApplyExamResponse = await apiService.applyForExam(
                selectedStudent!.memberId,
                rankData!.nextRankId
            );

            debugLog('📥 API Response:', response);

            if (response.success) {
                debugLog('✅ Exam application successful!');
                setApplyMessage(`Exam application submitted successfully!`);

                // Clear cache and refresh rank data after successful application
                debugLog('🧹 Clearing member cache...');
                apiService.clearMemberCache(selectedStudent!.memberId);

                debugLog('🔄 Fetching fresh rank data...');
                await fetchRankData(selectedStudent, true);

                debugLog('✅ Cache update completed!');

                // Auto-clear success message after 3 seconds
                setTimeout(() => {
                    setApplyMessage(null);
                    debugLog('🗑️ Success message cleared');
                }, 3000);
            } else {
                debugLog('❌ Exam application failed:', response.message);
                setApplyMessage(response.message || 'Failed to apply for exam');
            }
        } catch (error) {
            debugError('💥 Error applying for exam:', error);
            setApplyMessage('Failed to apply for exam. Please try again.');
        } finally {
            setApplyingForExam(false);
            debugLog('🏁 Exam application process completed');
        }
    };

    // Debug logging utility
    // To enable logging: window.DEBUG_LOGS = true
    // To disable logging: window.DEBUG_LOGS = false
    const debugLog = (...args: any[]) => {
        if (typeof window !== 'undefined' && (window as any).DEBUG_LOGS) {
            // eslint-disable-next-line no-console
            console.log(...args);
        }
    };

    const debugError = (...args: any[]) => {
        if (typeof window !== 'undefined' && (window as any).DEBUG_LOGS) {
            // eslint-disable-next-line no-console
            console.error(...args);
        }
    };

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />
            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={() => window.location.href = '/'}
                selectedStudent={selectedStudent}
            />
            <main className="home-content">
                <div className="content-container">
                    {/* Welcome Section */}
                    <Card className="welcome-card">
                        <Flex align="center" gap="4">
                            <Avatar
                                size="4"
                                src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                            />
                            <Flex direction="column" gap="1">
                                <Heading size="6">Welcome, {selectedStudent?.fullName || 'Student'}!</Heading>
                                <Text size="2" color="gray">Here is your rank progression and achievements.</Text>
                            </Flex>
                        </Flex>
                    </Card>

                    {/* Stats Section */}
                    <div className="profile-stats-grid">
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="blue">
                                    {currentRank?.rank_name || 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Current Rank</Text>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="green">
                                    {rankData?.nextRank || 'N/A'}
                                </Text>
                                <Text size="2" color="gray">Next Rank</Text>
                                {rankData?.nextRank && (
                                    <button
                                        style={{
                                            backgroundColor:
                                                applyingForExam || pendingCount >= 3 || profile?.membershipStatus === 'EXPIRED'
                                                    ? 'var(--gray-6)'
                                                    : 'var(--green-9)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            cursor:
                                                applyingForExam || pendingCount >= 3 || profile?.membershipStatus === 'EXPIRED'
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                            marginTop: '4px',
                                            opacity:
                                                applyingForExam || pendingCount >= 3 || profile?.membershipStatus === 'EXPIRED'
                                                    ? 0.7
                                                    : 1
                                        }}
                                        onClick={handleApplyClick}
                                        disabled={applyingForExam || pendingCount >= 3 || profile?.membershipStatus === 'EXPIRED'}
                                    >
                                        {applyingForExam
                                            ? '🔄 Applying...'
                                            : 'Apply for Next Rank'}
                                    </button>
                                )}
                                {profile?.membershipStatus === 'EXPIRED' && (
                                    <Text
                                        size="1"
                                        color="red"
                                        style={{ marginTop: '4px', textAlign: 'center' }}
                                    >
                                        You must have an active membership to apply for the next rank.
                                    </Text>
                                )}

                                {/* Debug/Refresh Button */}
                                <button
                                    style={{
                                        backgroundColor: 'var(--blue-9)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '10px',
                                        cursor: 'pointer',
                                        marginTop: '4px'
                                    }}
                                    onClick={() => {
                                        debugLog('🔄 Manual refresh triggered');
                                        apiService.clearMemberCache(selectedStudent!.memberId);
                                        fetchRankData(selectedStudent, false); // Don't preserve apply message for manual refresh
                                    }}
                                >
                                    🔄 Refresh Data
                                </button>
                            </Flex>
                        </Card>
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="orange">
                                    {totalRanks}
                                </Text>
                                <Text size="2" color="gray">Ranks Earned</Text>
                            </Flex>
                        </Card>
                    </div>

                    {/* Current Rank Details */}
                    <Card className="activity-card" style={{ padding: '16px' }}>
                        <Heading size="4" mb="3">Current Rank Details</Heading>
                        {loading ? (
                            <Text size="3">Loading rank details...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : currentRank ? (
                            <div className="profile-stats-grid">
                                <Card className="stat-card" style={{ padding: '12px' }}>
                                    <Flex direction="column" gap="1" align="center">
                                        <div className="activity-icon" style={{ marginBottom: '4px' }}>
                                            <StarIcon width="16" height="16" />
                                        </div>
                                        <Text size="3" weight="bold" color="blue">
                                            {currentRank.rank_name}
                                        </Text>
                                        <Text size="1" color="gray">Current Rank</Text>
                                    </Flex>
                                </Card>
                                <Card className="stat-card" style={{ padding: '12px' }}>
                                    <Flex direction="column" gap="1" align="center">
                                        <div className="activity-icon" style={{ marginBottom: '4px' }}>
                                            <TargetIcon width="16" height="16" />
                                        </div>
                                        <Text size="3" weight="bold" color="green">
                                            {currentRank.certificateNumber}
                                        </Text>
                                        <Text size="1" color="gray">Certificate Number</Text>
                                    </Flex>
                                </Card>
                                <Card className="stat-card" style={{ padding: '12px' }}>
                                    <Flex direction="column" gap="1" align="center">
                                        <div className="activity-icon" style={{ marginBottom: '4px' }}>
                                            <CalendarIcon width="16" height="16" />
                                        </div>
                                        <Text size="3" weight="bold" color="orange">
                                            {currentRank.issueDate}
                                        </Text>
                                        <Text size="1" color="gray">Issue Date</Text>
                                    </Flex>
                                </Card>
                            </div>
                        ) : (
                            <Text size="3">No rank data available</Text>
                        )}
                    </Card>

                    {/* Rank History */}
                    <Card className="activity-card">
                        <Heading size="4" mb="4">Rank History</Heading>
                        {loading ? (
                            <Text size="3">Loading rank history...</Text>
                        ) : error ? (
                            <Text size="3" color="red">{error}</Text>
                        ) : rankData?.ranks ? (
                            <Flex direction="column" gap="3">
                                {rankData.ranks.map((rank, index) => {
                                    const isPending = !rank.certificateNumber || rank.certificateNumber === 'Pending';
                                    return (
                                        <Flex key={rank.rankId} align="center" gap="3" className="activity-item">
                                            <div className="activity-icon" style={{ backgroundColor: getRankColor(rank.rank_name) }}>
                                                <StarIcon width="16" height="16" />
                                            </div>
                                            <Flex direction="column" gap="1">
                                                <Text size="2" weight="bold">{rank.rank_name}</Text>
                                                <Text size="1" color="gray">
                                                    Issued: {rank.issueDate} | Certificate: {rank.certificateNumber ? rank.certificateNumber : 'Pending'}
                                                </Text>
                                            </Flex>
                                            <div className="activity-icon" style={{ backgroundColor: getRankColor(rank.rank_name), marginLeft: 'auto' }}>
                                                {isPending ? (
                                                    <ClockIcon width="16" height="16" />
                                                ) : (
                                                    <CheckIcon width="16" height="16" />
                                                )}
                                            </div>
                                        </Flex>
                                    );
                                })}
                            </Flex>
                        ) : (
                            <Text size="3">No rank history available</Text>
                        )}
                    </Card>
                </div>
            </main>

            {/* Confirmation Dialog */}
            <Dialog.Root open={showConfirmDialog} onOpenChange={(open) => {
                setShowConfirmDialog(open);
                if (!open) {
                    setApplyMessage(null); // Clear any previous messages when dialog closes
                }
            }}>
                <Dialog.Content style={{ maxWidth: 450 }}>
                    <Dialog.Title>Confirm Exam Application</Dialog.Title>
                    <Dialog.Description size="2" mb="4">
                        Are you sure you want to apply for the {rankData?.nextRank} exam?
                    </Dialog.Description>

                    <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                            <Button variant="soft" color="gray">
                                Cancel
                            </Button>
                        </Dialog.Close>
                        <Button
                            onClick={handleApplyForExam}
                            disabled={applyingForExam}
                            style={{
                                backgroundColor: 'var(--green-9)',
                                color: 'white'
                            }}
                        >
                            {applyingForExam ? 'Applying...' : 'Confirm Apply'}
                        </Button>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>

            {/* Success/Error Message Toast */}
            {applyMessage && (
                <div
                    className="toast-notification"
                    style={{
                        backgroundColor: (() => {
                            const isSuccess = applyMessage.includes('Successfully') ||
                                applyMessage.includes('successfully') ||
                                applyMessage.includes('success') ||
                                applyMessage.includes('Success');
                            debugLog('🎨 Toast color logic:', {
                                message: applyMessage,
                                isSuccess: isSuccess,
                                willShowGreen: isSuccess
                            });
                            return isSuccess ? 'var(--green-9)' : 'var(--red-9)';
                        })(),
                        color: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1001,
                        animation: 'slideIn 0.3s ease-out',
                        wordWrap: 'break-word'
                    }}
                    onAnimationStart={() => debugLog('🎬 Toast animation started')}
                    onAnimationEnd={() => debugLog('🎬 Toast animation ended')}
                >
                    <Flex align="center" gap="2">
                        {(() => {
                            const isSuccess = applyMessage.includes('Successfully') ||
                                applyMessage.includes('successfully') ||
                                applyMessage.includes('success') ||
                                applyMessage.includes('Success');
                            return isSuccess ? (
                                <CheckIcon width="16" height="16" />
                            ) : (
                                <ClockIcon width="16" height="16" />
                            );
                        })()}
                        <Text size="2" weight="bold">{applyMessage}</Text>
                    </Flex>
                </div>
            )}
        </div>
    );
};

export default RankDetails; 